import { HospitalIcon, Siren, Stethoscope } from 'lucide-react';
import { motion } from 'motion/react';
import type { ReactNode } from 'react';

import { useI18n } from '../../i18n';

const serviceIcons: Record<string, ReactNode> = {
  ambulances: (
    <svg viewBox="0 0 64 64" fill="currentColor" className="h-7 w-7 opacity-80">
      <path d="M56 36h-2v-8a2 2 0 0 0-.59-1.41l-8-8A2 2 0 0 0 44 18H38V14a2 2 0 0 0-2-2H10a2 2 0 0 0-2 2v26a2 2 0 0 0 2 2h2a8 8 0 0 0 16 0h8a8 8 0 0 0 16 0h4a2 2 0 0 0 2-2v-2a2 2 0 0 0-2-2zM20 46a4 4 0 1 1 4-4 4 4 0 0 1-4 4zm8-10H12V16h22v20zm16 10a4 4 0 1 1 4-4 4 4 0 0 1-4 4zm-6-10V22h5.17L50 28.83V36z" />
      <path d="M26 22h-4v4h-4v4h4v4h4v-4h4v-4h-4z" />
    </svg>
  ),
  'mobile-clinics': <HospitalIcon className="h-7 w-7" strokeWidth={1.5} />,
  'medical-teams': <Siren className="h-7 w-7" strokeWidth={1.5} />,
  'medical-equipment': <Stethoscope className="h-7 w-7" strokeWidth={1.5} />,
};

const defaultStyle = {
  border: 'border-t-emerald-500',
  iconBg: 'bg-emerald-50',
  iconColor: 'text-emerald-600',
};

export default function ServicesSection() {
  const { t } = useI18n();

  return (
    <motion.section
      id="services"
      className="overflow-x-hidden px-4 py-12 sm:px-6 sm:py-16 md:px-8 md:py-20 lg:px-32"
      initial={{ opacity: 0 }}
      whileInView={{ opacity: 1 }}
      transition={{ duration: 0.8 }}
      viewport={{ once: true, margin: '-100px' }}
    >
      <motion.div
        className="mb-10 text-center"
        initial={{ y: 50, opacity: 0 }}
        whileInView={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.8, ease: 'easeOut' }}
        viewport={{ once: true }}
      >
        <motion.div
          className="flex flex-col items-center justify-center gap-6"
          initial={{ y: 30, opacity: 0 }}
          whileInView={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.2, duration: 0.6 }}
          viewport={{ once: true }}
        >
          <h2 className="text-2xl font-bold text-gray-900 sm:text-3xl md:text-4xl">
            {t.services.title}
          </h2>
          <motion.div
            className="mx-auto h-1 w-full max-w-4xl bg-emerald-500"
            initial={{ scaleX: 0 }}
            whileInView={{ scaleX: 1 }}
            transition={{ delay: 0.4, duration: 0.8 }}
            viewport={{ once: true }}
          />
        </motion.div>
      </motion.div>

      <div className="mx-auto grid max-w-4xl grid-cols-1 gap-5 sm:grid-cols-2">
        {t.services.items.map((service, index) => {
          const styles = defaultStyle;
          return (
            <motion.div
              key={service.id}
              className={`overflow-hidden rounded-xl border-t-4 bg-white p-6 shadow-sm transition-shadow hover:shadow-md ${styles.border}`}
              initial={{ y: 60, opacity: 0 }}
              whileInView={{ y: 0, opacity: 1 }}
              transition={{
                delay: 0.1 + index * 0.1,
                duration: 0.5,
                ease: 'easeOut',
              }}
              viewport={{ once: true }}
            >
              <div className="mb-4 flex items-center gap-3">
                <div
                  className={`flex h-12 w-12 items-center justify-center rounded-full ${styles.iconBg} ${styles.iconColor}`}
                >
                  {serviceIcons[service.id] || <Stethoscope className="h-7 w-7" strokeWidth={1.5} />}
                </div>
                <h3 className="text-lg font-bold text-gray-800">
                  {service.title}
                </h3>
              </div>
              <p className="text-sm leading-relaxed text-gray-600">
                {service.description}
              </p>
            </motion.div>
          );
        })}
      </div>
    </motion.section>
  );
}
